import { Link } from "react-router-dom";
import { Home, ShieldCheck, PhoneCall } from "lucide-react";
import ChatWidget from "@/components/gentry/ChatWidget";
import { BUSINESS } from "@/lib/business";

export default function ChatPage() {
  return (
    <div className="min-h-screen bg-[#F9F6F0] relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/28122111/pexels-photo-28122111.jpeg')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#F9F6F0]/85 via-[#F9F6F0]/70 to-[#F9F6F0]/95" />

      {/* Nav */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1E3B2F] flex items-center justify-center text-white">
            <Home size={18} />
          </div>
          <div>
            <div className="font-serif-display text-2xl leading-none text-[#1E3B2F]">
              Gens Realty
            </div>
            <div className="font-label text-[9px] text-[#6B7280] mt-1">
              Houston · TX · Since 2018
            </div>
          </div>
        </div>
        <Link
          to="/admin/login"
          className="flex items-center gap-2 text-xs font-medium text-[#1E3B2F] hover:text-[#C25934] transition-colors"
          data-testid="staff-login-link"
        >
          <ShieldCheck size={14} />
          Staff Login
        </Link>
      </header>

      {/* Hero + Chat */}
      <main className="relative z-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start px-6 md:px-12 pb-16 max-w-7xl mx-auto">
        <section className="pt-6 lg:pt-16">
          <span
            className="inline-block font-label text-[10px] text-[#C25934] mb-6"
            data-testid="hero-badge"
          >
            Gens Realty · Houston, TX
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1F1F1F]">
            Meet <span className="italic text-[#C25934]">Gentry</span>,
            <br />
            your always-on
            <br />
            receptionist.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#3d3d3d] max-w-xl leading-relaxed">
            Warm Southern hospitality — in English or Kreyòl Ayisyen. Ask about
            our services, leave a message, or book an appointment. Any hour, any day.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={`tel:${BUSINESS.phoneTel}`}
              className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[#C25934] hover:bg-[#a44827] text-white text-sm font-semibold btn-lift shadow-lg shadow-[#C25934]/20"
              data-testid="hero-call-button"
            >
              <PhoneCall size={16} />
              <span>Call {BUSINESS.phone}</span>
            </a>
            <span className="text-xs text-[#6B7280] max-w-[16rem]">
              Prefer to talk to a real person? Give us a call — or keep chatting with Gentry.
            </span>
          </div>

          <div className="mt-10 space-y-3 max-w-md">
            <FactRow k="Business hours" v="Mon–Fri 9a–6p · Sat by appt · Closed Sun" />
            <FactRow k="Location" v="Houston, TX" />
            <FactRow
              k="Services"
              v="Home Sales · Rentals · Property Management (coming soon)"
            />
            <FactRow k="Languages" v="English · Kreyòl Ayisyen" />
          </div>
        </section>

        <section className="pt-4 lg:pt-10">
          <ChatWidget />
        </section>
      </main>

      <footer className="relative z-10 border-t border-[#EBE4D3] py-6 text-center text-xs text-[#6B7280]">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Link
            to="/sms"
            className="hover:text-[#1E3B2F] transition-colors"
            data-testid="footer-sms-link"
          >
            SMS Opt-In
          </Link>
          <span className="text-[#EBE4D3]">·</span>
          <Link
            to="/terms"
            className="hover:text-[#1E3B2F] transition-colors"
            data-testid="footer-terms-link"
          >
            Terms of Service
          </Link>
          <span className="text-[#EBE4D3]">·</span>
          <Link
            to="/privacy"
            className="hover:text-[#1E3B2F] transition-colors"
            data-testid="footer-privacy-link"
          >
            Privacy Policy
          </Link>
        </div>
        © {new Date().getFullYear()} Gens Realty · Powered by Gentry AI
      </footer>
    </div>
  );
}

function FactRow({ k, v }) {
  return (
    <div className="flex items-start gap-4 py-2 border-b border-[#EBE4D3]/60">
      <span className="font-label text-[10px] text-[#6B7280] w-28 pt-0.5 shrink-0">{k}</span>
      <span className="text-sm text-[#1F1F1F]">{v}</span>
    </div>
  );
}
